/**
 * Scrolling Banner — Custom element for carousel with autoplay, play/pause,
 * indicator dots, and prev/next navigation.
 */
if (!customElements.get('scrolling-banner')) {
  class ScrollingBanner extends HTMLElement {
    constructor() {
      super();

      this.slides = this.querySelectorAll('.pd-scrolling-banner__slide');
      this.dots = this.querySelectorAll('.pd-scrolling-banner__dot');
      this.prevBtn = this.querySelector('.pd-scrolling-banner__nav-btn--prev');
      this.nextBtn = this.querySelector('.pd-scrolling-banner__nav-btn--next');
      this.playPauseBtn = this.querySelector('.pd-scrolling-banner__playpause');

      this.currentIndex = 0;
      this.slideCount = parseInt(this.getAttribute('data-slide-count'), 10) || 0;
      this.autoplaySpeed = parseInt(this.getAttribute('data-autoplay-speed'), 10) || 5000;
      this.isPlaying = this.getAttribute('data-autoplay') === 'true';
      this.autoplayTimer = null;

      this.init();
    }

    init() {
      if (this.slideCount <= 0) return;

      this.bindEvents();

      if (this.isPlaying) {
        this.startAutoplay();
      }
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      if (this.playPauseBtn) {
        this.playPauseBtn.addEventListener('click', () => this.toggleAutoplay());
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.getAttribute('data-slide-index'), 10);
          this.goToSlide(index);
        });
      });

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prev();
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          this.next();
          e.preventDefault();
        }
      });

      // Pause on hover, resume on leave (only if autoplay was active)
      this.addEventListener('mouseenter', () => {
        if (this.isPlaying) {
          this.stopAutoplay(false);
        }
      });

      this.addEventListener('mouseleave', () => {
        if (this.isPlaying) {
          this.startAutoplay();
        }
      });

      // Pause when not visible (IntersectionObserver)
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting && this.isPlaying) {
                this.stopAutoplay(false);
              } else if (entry.isIntersecting && this.isPlaying) {
                this.startAutoplay();
              }
            });
          },
          { threshold: 0.3 }
        );
        this.observer.observe(this);
      }
    }

    goToSlide(index) {
      if (index === this.currentIndex || index < 0 || index >= this.slideCount) return;

      // Update slides
      this.slides[this.currentIndex].classList.remove('is-active');
      this.slides[index].classList.add('is-active');

      // Update dots
      this.dots[this.currentIndex].classList.remove('is-active');
      this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
      this.dots[index].classList.add('is-active');
      this.dots[index].setAttribute('aria-selected', 'true');

      this.currentIndex = index;

      // Reset autoplay timer on manual interaction
      if (this.isPlaying) {
        this.resetAutoplay();
      }
    }

    next() {
      const nextIndex = (this.currentIndex + 1) % this.slideCount;
      this.goToSlide(nextIndex);
    }

    prev() {
      const prevIndex = (this.currentIndex - 1 + this.slideCount) % this.slideCount;
      this.goToSlide(prevIndex);
    }

    startAutoplay() {
      this.stopAutoplay(false);
      this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
    }

    stopAutoplay(updateState) {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      if (updateState) {
        this.isPlaying = false;
        this.updatePlayPauseButton();
      }
    }

    resetAutoplay() {
      if (this.isPlaying) {
        this.stopAutoplay(false);
        this.startAutoplay();
      }
    }

    toggleAutoplay() {
      if (this.isPlaying) {
        this.isPlaying = false;
        this.stopAutoplay(false);
      } else {
        this.isPlaying = true;
        this.startAutoplay();
      }
      this.updatePlayPauseButton();
    }

    updatePlayPauseButton() {
      if (!this.playPauseBtn) return;
      this.playPauseBtn.setAttribute('data-playing', this.isPlaying.toString());
      this.playPauseBtn.setAttribute(
        'aria-label',
        this.isPlaying ? 'Pause slideshow' : 'Play slideshow'
      );
    }

    disconnectedCallback() {
      this.stopAutoplay(false);
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  customElements.define('scrolling-banner', ScrollingBanner);
}
